import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/layout/Layout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Search, X, SlidersHorizontal, MapPin, Clock, Tag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'all', label: 'Todas las categorías' },
  { value: 'familia', label: 'Viajes de Familia' },
  { value: 'novios', label: 'Viajes de Novios' },
  { value: 'grupos', label: 'Viajes de Grupos' },
  { value: 'cruceros', label: 'Cruceros' },
  { value: 'bienestar', label: 'Bienestar y Relax' },
  { value: 'arte', label: 'Arte e Historia' },
  { value: 'lujo', label: 'Lujo y Compras' },
  { value: 'deportivo', label: 'Deportivo y Cultural' },
  { value: 'enologico', label: 'Enológicos y Gastronómicos' },
  { value: 'naturaleza', label: 'Naturaleza y Aventura' },
  { value: 'medida', label: 'Viajes a Medida' },
];

const DURATIONS = [
  { value: 'all', label: 'Cualquier duración' },
  { value: 'short', label: '1-5 días' },
  { value: 'medium', label: '6-10 días' },
  { value: 'long', label: 'Más de 10 días' },
];

export default function ViajesDestacados() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [pageData, setPageData] = useState(null);
  
  // Filter states
  const [searchText, setSearchText] = useState('');
  const [category, setCategory] = useState('all');
  const [duration, setDuration] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [destination, setDestination] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tripsRes, pageRes] = await Promise.all([
          axios.get(`${API}/trips`),
          axios.get(`${API}/pages/viajes?t=${Date.now()}`)
        ]);
        setTrips(tripsRes.data);
        setPageData(pageRes.data);
      } catch (err) {
        console.error('Error fetching trips:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (pageData) {
      document.title = pageData.seo_title || "Explorar Viajes | Viajes de Ensueño";
      const description = pageData.seo_description || "Descubre nuestra selección de viajes exclusivos y experiencias personalizadas.";
      document.querySelector('meta[name="description"]')?.setAttribute("content", description);
    }
  }, [pageData]);

  // Get unique destinations for suggestions
  const destinations = useMemo(() => {
    const unique = [...new Set(trips.map(t => t.destination))];
    return unique.sort();
  }, [trips]);

  // Filter logic
  const filteredTrips = useMemo(() => {
    return trips.filter(trip => {
      // Text search (title, description, destination)
      if (searchText) {
        const search = searchText.toLowerCase();
        const matchesText = 
          trip.title.toLowerCase().includes(search) ||
          trip.description.toLowerCase().includes(search) ||
          trip.destination.toLowerCase().includes(search) ||
          trip.short_description.toLowerCase().includes(search);
        if (!matchesText) return false;
      }

      // Category filter
      if (category !== 'all' && trip.category !== category) {
        return false;
      }

      // Destination filter
      if (destination && !trip.destination.toLowerCase().includes(destination.toLowerCase())) {
        return false;
      }

      // Price range filter
      if (trip.price < priceRange[0] || trip.price > priceRange[1]) {
        return false;
      }

      // Duration filter
      if (duration !== 'all') {
        const daysMatch = trip.duration.match(/(\d+)/);
        const days = daysMatch ? parseInt(daysMatch[1]) : 0;
        
        if (duration === 'short' && days > 5) return false;
        if (duration === 'medium' && (days < 6 || days > 10)) return false;
        if (duration === 'long' && days <= 10) return false;
      }

      return true;
    });
  }, [trips, searchText, category, destination, priceRange, duration]);

  const clearFilters = () => {
    setSearchText('');
    setCategory('all');
    setDuration('all');
    setPriceRange([0, 10000]);
    setDestination('');
  };

  const hasActiveFilters = searchText || category !== 'all' || duration !== 'all' || 
    priceRange[0] > 0 || priceRange[1] < 10000 || destination;

  return (
    <Layout>
      {/* Hero Dynamic */}
      <div className="relative h-[40vh] min-h-[300px]">
        <img
          src={pageData?.hero_image ? `${process.env.REACT_APP_BACKEND_URL}${pageData.hero_image}` : "https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&q=80&w=2000"}
          alt={pageData?.title || "Viajes Destacados"}
          className="w-full h-full object-cover"
        />
        <div className="hero-gradient absolute inset-0" />
        
        <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-16">
          <div className="container-custom">
            <h1 className="text-4xl lg:text-6xl font-serif text-white mb-4" data-testid="featured-title">
              {pageData?.title || "Todos los Viajes"}
            </h1>
            <p className="text-xl text-white/80 max-w-2xl">
              {pageData?.subtitle || "Encuentra tu próxima aventura entre nuestras ofertas"}
            </p>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <section className="py-8 bg-white border-b sticky top-16 z-30">
        <div className="container-custom">
          {/* Main search bar */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar por destino, nombre de viaje..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                data-testid="search-input"
                className="pl-12 h-12 text-lg"
              />
            </div>
            
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="toggle-filters"
              className={`h-12 px-6 ${showFilters ? 'bg-primary text-white' : ''}`}
            >
              <SlidersHorizontal className="w-5 h-5 mr-2" />
              Filtros
              {hasActiveFilters && (
                <span className="ml-2 w-5 h-5 rounded-full bg-accent text-white text-xs flex items-center justify-center">
                  !
                </span>
              )}
            </Button>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="h-12 text-muted-foreground"
              >
                <X className="w-4 h-4 mr-2" />
                Limpiar
              </Button>
            )}
          </div>

          {/* Expanded filters */}
          {showFilters && (
            <div className="mt-6 p-6 bg-slate-50 rounded-xl animate-fade-in" data-testid="filters-panel">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Category */}
                <div>
                  <label className="form-label">Tipo de viaje</label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="filter-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Destination */}
                <div>
                  <label className="form-label">Destino</label>
                  <Input
                    type="text"
                    placeholder="Ej: Maldivas, Bali..."
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    data-testid="filter-destination"
                    list="destinations"
                  />
                  <datalist id="destinations">
                    {destinations.map((d) => (
                      <option key={d} value={d} />
                    ))}
                  </datalist>
                </div>

                {/* Duration */}
                <div>
                  <label className="form-label">Duración</label>
                  <Select value={duration} onValueChange={setDuration}>
                    <SelectTrigger data-testid="filter-duration">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DURATIONS.map((dur) => (
                        <SelectItem key={dur.value} value={dur.value}>
                          {dur.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Price Range */}
                <div>
                  <label className="form-label">
                    Precio: {priceRange[0].toLocaleString('es-ES')}€ - {priceRange[1].toLocaleString('es-ES')}€
                  </label>
                  <Slider
                    value={priceRange}
                    onValueChange={setPriceRange}
                    min={0}
                    max={10000}
                    step={100}
                    data-testid="filter-price"
                    className="mt-3"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Results */}
      <section className="section bg-slate-50">
        <div className="container-custom">
          {/* Results count */}
          <div className="mb-8">
            <p className="text-muted-foreground">
              {loading ? 'Cargando...' : (
                <>
                  <span className="font-semibold text-primary">{filteredTrips.length}</span>
                  {' '}viajes encontrados
                  {hasActiveFilters && ' con los filtros aplicados'}
                </>
              )}
            </p>
          </div>

          {loading ? (
            <div className="grid-trips">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="trip-card skeleton h-[400px]" />
              ))}
            </div>
          ) : filteredTrips.length > 0 ? (
            <div className="grid-trips">
              {filteredTrips.map((trip, index) => (
                <Link
                  key={trip.id}
                  to={`/viaje/${trip.id}`}
                  className="trip-card group hover-lift"
                  data-testid={`search-trip-${index}`}
                >
                  <div className="trip-card-image-wrapper">
                    {trip.discount && (
                      <div className="absolute top-4 left-4 z-30 bg-accent text-white px-4 py-1.5 rounded-full font-bold text-sm shadow-xl flex items-center gap-2 animate-pulse border-2 border-white/20">
                        <Tag className="w-4 h-4" />
                        {trip.discount}
                      </div>
                    )}
                    <div 
                      className="absolute inset-0 bg-cover bg-center scale-110 blur-xl opacity-30"
                      style={{ backgroundImage: `url(${trip.image_url})` } }
                    />
                    <img
                      src={trip.image_url}
                      alt={trip.title}
                      className="relative z-10 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="trip-card-overlay z-20" />
                  </div>
                  <div className="trip-card-content">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-0.5 bg-accent/20 text-accent text-[10px] uppercase font-bold tracking-tighter rounded-full">
                        {CATEGORIES.find(c => c.value === trip.category)?.label || trip.category}
                      </span>
                    </div>
                    <span className="flex items-center gap-1 text-secondary text-xs uppercase tracking-widest font-bold">
                      {trip.destination}
                    </span>
                    <h3 className="text-xl font-serif font-bold mt-2 mb-4 line-clamp-2 min-h-[3.5rem]">
                      {trip.title}
                    </h3>
                    <div className="mt-auto flex items-center justify-between border-t border-white/10 pt-4">
                      <span className="flex items-center gap-1 text-white/70 text-sm">
                        <Clock className="w-4 h-4 text-accent" />
                        {trip.duration}
                      </span>
                      <div className="text-right">
                        <span className="block text-xs text-white/60 uppercase tracking-tighter">Desde</span>
                        <span className="text-accent font-bold text-xl">
                          {trip.price.toLocaleString('es-ES')}€
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-6">
                <Search className="w-10 h-10 text-slate-300" />
              </div>
              <h3 className="text-xl font-serif font-semibold text-primary mb-2">
                No encontramos viajes
              </h3>
              <p className="text-muted-foreground mb-6">
                Prueba a ajustar los filtros o buscar con otros términos
              </p>
              <Button onClick={clearFilters} className="btn-secondary">
                Limpiar filtros
              </Button>
            </div>
          )}
        </div>
      </section>

      <div className="h-32 bg-slate-50" />
    </Layout>
  );
}
